/**
 * HelpCenter - Centre d'aide ultra-moderne HIGH-TECH
 * Design glassmorphism, animations avancées, support voix TTS
 * @author COPPAM Platform
 */
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle, X, Search, BookOpen, Sparkles, MessageCircle, Zap, Award, RefreshCw, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Progress } from '../ui/progress';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { cn } from '../../lib/utils';
import { TUTORIALS, getTutorialsBySpace, getAllTutorials } from '../../data/tutorials';
import { AnimatedBackground, TutorialPlayer, TutorialCard } from './HelpCenterParts';

const HelpCenter = ({ space = 'member', user: user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTutorial, setActiveTutorial] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('tutorials');
  const [completedTutorials, setCompletedTutorials] = useState(() => {
    // Load completed tutorials from localStorage on init
    const completed = {};
    if (typeof window !== 'undefined') {
      Object.keys(TUTORIALS).forEach(id => {
        if (localStorage.getItem(`tutorial_${id}_completed`)) {
          completed[id] = true;
        }
      });
    }
    return completed;
  });

  const spaceTutorials = getTutorialsBySpace(space);
  const allTutorials = getAllTutorials();
  
  // Filter tutorials based on search
  const filteredTutorials = searchQuery
    ? allTutorials.filter(t => 
        t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.steps.some(s => s.title.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : spaceTutorials;

  const handleStartTutorial = (tutorial) => {
    setActiveTutorial(tutorial);
    setIsOpen(false);
  };

  const handleCompleteTutorial = () => {
    if (activeTutorial) {
      localStorage.setItem(`tutorial_${activeTutorial.id}_completed`, 'true');
      setCompletedTutorials(prev => ({ ...prev, [activeTutorial.id]: true }));
    }
    setActiveTutorial(null);
  };

  const handleResetTutorials = () => {
    Object.keys(TUTORIALS).forEach(id => {
      localStorage.removeItem(`tutorial_${id}_completed`);
    });
    setCompletedTutorials({});
  };

  const pendingCount = spaceTutorials.filter(t => !completedTutorials[t.id]).length;

  return (
    <>
      {/* Floating Help Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          "fixed bottom-6 right-6 z-50",
          "w-14 h-14 rounded-full",
          "bg-gradient-to-br from-violet-600 via-fuchsia-600 to-cyan-600",
          "shadow-lg shadow-violet-500/30",
          "flex items-center justify-center",
          "hover:scale-110 hover:shadow-xl hover:shadow-violet-500/40",
          "transition-all duration-300",
          "group"
        )}
      >
        <HelpCircle className="w-6 h-6 text-white group-hover:scale-110 transition-transform" />
        
        {/* Notification Badge */}
        {pendingCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs text-white font-bold flex items-center justify-center animate-pulse">
            {pendingCount}
          </span>
        )}
        
        {/* Ripple Effect */}
        <span className="absolute inset-0 rounded-full bg-white/20 animate-ping" style={{ animationDuration: '2s' }} />
      </button>

      {/* Help Panel */}
      {isOpen && createPortal(
        <div className="fixed inset-0 z-[90] flex justify-end">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Panel */}
          <div className="relative w-full max-w-lg bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 shadow-2xl animate-in slide-in-from-right duration-300">
            <AnimatedBackground />
            
            {/* Header */}
            <div className="relative z-10 p-6 border-b border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Centre d&apos;Aide</h2>
                    <p className="text-sm text-white/50">Tutoriels & Guide</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsOpen(false)}
                  className="text-white/60 hover:text-white hover:bg-white/10"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              {/* Search */}
              <div className="mt-4 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  placeholder="Rechercher un tutoriel..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-violet-500/50"
                />
              </div>
            </div>
            
            {/* Content */}
            <div className="relative z-10 h-[calc(100vh-200px)]">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
                <TabsList className="mx-6 mt-4 bg-white/5 border border-white/10">
                  <TabsTrigger value="tutorials" className="data-[state=active]:bg-violet-500/30 text-white/70 data-[state=active]:text-white">
                    <BookOpen className="w-4 h-4 mr-2" />
                    Tutoriels
                  </TabsTrigger>
                  <TabsTrigger value="shortcuts" className="data-[state=active]:bg-violet-500/30 text-white/70 data-[state=active]:text-white">
                    <Zap className="w-4 h-4 mr-2" />
                    Raccourcis
                  </TabsTrigger>
                  <TabsTrigger value="faq" className="data-[state=active]:bg-violet-500/30 text-white/70 data-[state=active]:text-white">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    FAQ
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="tutorials" className="flex-1 overflow-hidden px-6 pb-6">
                  <ScrollArea className="h-full">
                    {/* Progress Overview */}
                    <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-violet-500/10 to-cyan-500/10 border border-white/10">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-white/70">Progression</span>
                        <span className="text-sm font-bold text-white">
                          {Object.keys(completedTutorials).length}/{spaceTutorials.length}
                        </span>
                      </div>
                      <Progress 
                        value={(Object.keys(completedTutorials).length / spaceTutorials.length) * 100} 
                        className="h-2 bg-white/10" 
                      />
                      {Object.keys(completedTutorials).length === spaceTutorials.length && (
                        <p className="mt-2 text-sm text-green-400 flex items-center gap-2">
                          <Award className="w-4 h-4" />
                          Tous les tutoriels complétés ! 🎉
                        </p>
                      )}
                    </div>
                    
                    {/* Tutorial List */}
                    <div className="space-y-3">
                      {filteredTutorials.length === 0 ? (
                        <div className="text-center py-8">
                          <Search className="w-12 h-12 mx-auto text-white/20 mb-4" />
                          <p className="text-white/50">Aucun tutoriel trouvé</p>
                        </div>
                      ) : (
                        filteredTutorials.map((tutorial) => (
                          <TutorialCard
                            key={tutorial.id}
                            tutorial={tutorial}
                            completed={completedTutorials[tutorial.id]}
                            onStart={() => handleStartTutorial(tutorial)}
                          />
                        ))
                      )}
                    </div>
                    
                    {/* Reset Button */}
                    {Object.keys(completedTutorials).length > 0 && (
                      <Button
                        variant="ghost"
                        onClick={handleResetTutorials}
                        className="mt-6 w-full text-white/40 hover:text-white/60"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Réinitialiser les tutoriels
                      </Button>
                    )}
                  </ScrollArea>
                </TabsContent>
                
                <TabsContent value="shortcuts" className="flex-1 overflow-hidden px-6 pb-6">
                  <ScrollArea className="h-full">
                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider">
                        Raccourcis clavier
                      </h3>
                      {[
                        { key: 'Ctrl + K', action: 'Recherche rapide' },
                        { key: 'Ctrl + N', action: 'Nouvelle souscription' },
                        { key: 'Ctrl + H', action: 'Centre d\'aide' },
                        { key: 'Échap', action: 'Fermer le modal' },
                        { key: '?', action: 'Afficher les raccourcis' }
                      ].map((shortcut, i) => (
                        <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10">
                          <span className="text-white/70">{shortcut.action}</span>
                          <kbd className="px-2 py-1 bg-white/10 rounded text-xs font-mono text-violet-400">
                            {shortcut.key}
                          </kbd>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </TabsContent>
                
                <TabsContent value="faq" className="flex-1 overflow-hidden px-6 pb-6">
                  <ScrollArea className="h-full">
                    <div className="space-y-4">
                      {[
                        { 
                          q: 'Comment créer une nouvelle souscription ?', 
                          a: 'Accédez au simulateur BATICO, renseignez votre CA et sélectionnez vos activités.' 
                        },
                        { 
                          q: 'Comment déclarer un dommage ?', 
                          a: 'Depuis votre espace, cliquez sur "Dommages" puis "Nouvelle déclaration".' 
                        },
                        { 
                          q: 'Comment contacter le support ?', 
                          a: 'Utilisez la messagerie intégrée ou envoyez un email à support@coppam.fr' 
                        },
                        { 
                          q: 'Où trouver mes documents ?', 
                          a: 'Tous vos documents sont accessibles dans la section "Documents" de votre espace.' 
                        }
                      ].map((faq, i) => (
                        <div key={i} className="p-4 rounded-xl bg-white/5 border border-white/10">
                          <h4 className="font-semibold text-white mb-2">{faq.q}</h4>
                          <p className="text-sm text-white/60">{faq.a}</p>
                        </div>
                      ))}
                      
                      <Button className="w-full mt-4 bg-gradient-to-r from-violet-500 to-cyan-500">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Voir toute la FAQ
                      </Button>
                    </div>
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Tutorial Player */}
      {activeTutorial && (
        <TutorialPlayer
          tutorial={activeTutorial}
          onClose={() => setActiveTutorial(null)}
          onComplete={handleCompleteTutorial}
        />
      )}
    </>
  );
};

export default HelpCenter;
